import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { MessageCircle, Mail, Copy } from "lucide-react";

// WhatsApp click-to-chat (no provider account needed) + email fallback for receipts/invoices.
export function ShareDocButton({ shareUrl, emailUrl, defaultPhone = "", defaultEmail = "", label = "Send to customer", size = "sm", variant = "outline", testId = "share-doc-button" }) {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState(defaultPhone);
  const [email, setEmail] = useState(defaultEmail);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const openDialog = async () => {
    try { const { data } = await api.get(shareUrl); setText(data.text); if (data.phone && !phone) setPhone(data.phone); setOpen(true); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const waLink = () => {
    const digits = phone.replace(/\D/g, "");
    const intl = digits.startsWith("0") ? "27" + digits.slice(1) : digits;
    return `https://wa.me/${intl}?text=${encodeURIComponent(text)}`;
  };
  const sendEmail = async () => {
    setBusy(true);
    try { await api.post(emailUrl, { email }); toast.success(`Emailed to ${email}`); setOpen(false); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Button size={size} variant={variant} onClick={openDialog} data-testid={testId}><MessageCircle className="h-4 w-4 mr-1.5" /> {label}</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display text-xl">Send to customer</DialogTitle></DialogHeader>
          <pre className="text-xs bg-secondary rounded-md p-3 whitespace-pre-wrap max-h-40 overflow-y-auto font-mono" data-testid="share-preview">{text}</pre>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label className="text-xs uppercase text-muted-foreground">WhatsApp number</Label>
              <div className="flex gap-2">
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="082 123 4567" data-testid="share-phone-input" />
                <a href={phone.replace(/\D/g, "").length >= 9 ? waLink() : undefined} target="_blank" rel="noreferrer" onClick={(e) => { if (phone.replace(/\D/g, "").length < 9) { e.preventDefault(); toast.error("Enter a valid phone number"); } }}>
                  <Button className="bg-[#25D366] hover:bg-[#1DA851] text-white" data-testid="share-whatsapp-button"><MessageCircle className="h-4 w-4 mr-1.5" /> WhatsApp</Button>
                </a>
              </div>
              <p className="text-[11px] text-muted-foreground">Opens WhatsApp on this device with the receipt pre-filled — just tap send.</p>
            </div>
            <div className="space-y-1.5"><Label className="text-xs uppercase text-muted-foreground">Or email</Label>
              <div className="flex gap-2">
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="customer@example.com" data-testid="share-email-input" />
                <Button variant="outline" onClick={sendEmail} disabled={busy || !email.includes("@")} data-testid="share-email-button"><Mail className="h-4 w-4 mr-1.5" /> Email</Button>
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={async () => { try { await navigator.clipboard.writeText(text); toast.success("Copied"); } catch { toast.error("Clipboard blocked — select the text above and copy manually"); } }} data-testid="share-copy-button"><Copy className="h-4 w-4 mr-1.5" /> Copy text</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
